export const DEFAULT_PICKUP_TIME = '08:00';
export const DEFAULT_RETURN_TIME = '08:00';

export const HALF_DAY_PICKUP_OPTIONS = ['08:00', '13:00'];
export const HALF_DAY_RETURN_OPTIONS = ['12:00', '17:00'];
export const RENTAL_TIME_START = '08:00';
export const RENTAL_TIME_END = '17:00';

function timeToMinutes(time) {
  const parts = String(time || '00:00').split(':');
  const h = Number(parts[0]) || 0;
  const m = Number(parts[1]) || 0;
  return h * 60 + m;
}

function minutesToTime(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Halfuur-tijden (08:00 t/m 17:00), gelijk aan klassiek reserveringsformulier. */
export function buildHalfHourTimeOptions(start = RENTAL_TIME_START, end = RENTAL_TIME_END) {
  const options = [];
  let minutes = timeToMinutes(start);
  const endMinutes = timeToMinutes(end);
  while (minutes <= endMinutes) {
    options.push(minutesToTime(minutes));
    minutes += 30;
  }
  return options;
}

export const RENTAL_TIME_OPTIONS = buildHalfHourTimeOptions();

/** Snap ongeldige of per-minuut waarden naar dichtstbijzijnde halfuur-optie. */
export function normalizeRentalTime(time, options = RENTAL_TIME_OPTIONS) {
  if (!options.length) return DEFAULT_PICKUP_TIME;
  if (!time) return options[0];
  if (options.includes(time)) return time;

  const snapped = Math.round(timeToMinutes(time) / 30) * 30;
  const snappedStr = minutesToTime(snapped);
  if (options.includes(snappedStr)) return snappedStr;

  const first = timeToMinutes(options[0]);
  const last = timeToMinutes(options[options.length - 1]);
  if (snapped <= first) return options[0];
  if (snapped >= last) return options[options.length - 1];

  let closest = options[0];
  let minDiff = Infinity;
  for (const opt of options) {
    const diff = Math.abs(timeToMinutes(opt) - snapped);
    if (diff < minDiff) {
      minDiff = diff;
      closest = opt;
    }
  }
  return closest;
}

const HALF_DAY_RETURN_FOR_PICKUP = { '08:00': '12:00', '13:00': '17:00' };
const HALF_DAY_PICKUP_FOR_RETURN = { '12:00': '08:00', '17:00': '13:00' };

export function getHalfDayReturnTime(pickupTime) {
  if (HALF_DAY_RETURN_FOR_PICKUP[pickupTime]) return HALF_DAY_RETURN_FOR_PICKUP[pickupTime];
  if (!pickupTime) return '12:00';
  return pickupTime < '13:00' ? '12:00' : '17:00';
}

/** Halve dag: ophalen 08:00 → inleveren 12:00; ophalen 13:00 → inleveren 17:00. */
export function syncHalfDayTimes(formState, source = 'pickup') {
  if (!formState || formState.rentalType !== 'halve_dag') return;

  if (source === 'return') {
    const pickup = HALF_DAY_PICKUP_FOR_RETURN[formState.returnTime];
    if (pickup) {
      formState.pickupTime = pickup;
      return;
    }
  }

  if (!formState.pickupTime) formState.pickupTime = DEFAULT_PICKUP_TIME;
  if (formState.pickupTime === '08:00' || formState.pickupTime === '13:00') {
    formState.returnTime = HALF_DAY_RETURN_FOR_PICKUP[formState.pickupTime];
    return;
  }

  formState.pickupTime = formState.pickupTime < '13:00' ? '08:00' : '13:00';
  formState.returnTime = HALF_DAY_RETURN_FOR_PICKUP[formState.pickupTime];

  if (source === 'return') {
    const pickup = HALF_DAY_PICKUP_FOR_RETURN[formState.returnTime];
    if (pickup) formState.pickupTime = pickup;
    formState.returnTime = HALF_DAY_RETURN_FOR_PICKUP[formState.pickupTime];
  }
}

/** Zet lege tijdvelden op standaard (08:00; halve dag inleveren 12:00). */
export function ensureDefaultRentalTimes(formState) {
  if (!formState) return;
  const hasDates = !!(formState.pickupDate || formState.returnDate);
  if (!hasDates) return;
  if (formState.rentalType === 'halve_dag') {
    if (!formState.pickupTime) formState.pickupTime = DEFAULT_PICKUP_TIME;
    syncHalfDayTimes(formState, 'pickup');
    return;
  }
  if (!formState.pickupTime) formState.pickupTime = DEFAULT_PICKUP_TIME;
  else formState.pickupTime = normalizeRentalTime(formState.pickupTime);
  if (!formState.returnTime) formState.returnTime = DEFAULT_RETURN_TIME;
  else formState.returnTime = normalizeRentalTime(formState.returnTime);
}

function fmtDate(d) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = `0${d.getMonth() + 1}`.slice(-2);
  const dd = `0${d.getDate()}`.slice(-2);
  return `${y}-${m}-${dd}`;
}

function nextAvail(date, requireWorkday) {
  if (typeof window.getNextAvailableDate === 'function') {
    try {
      return window.getNextAvailableDate(new Date(date), !!requireWorkday);
    } catch {
      /* fall through */
    }
  }
  const d = new Date(date);
  d.setDate(d.getDate() + 1);
  if (requireWorkday) {
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  } else if (d.getDay() === 0) {
    d.setDate(d.getDate() + 1);
  }
  if (typeof window.isClosedDay === 'function') {
    while (window.isClosedDay(d)) d.setDate(d.getDate() + 1);
  }
  return d;
}

function skipClosed(d) {
  if (typeof window.isClosedDay !== 'function') return d;
  while (window.isClosedDay(d)) d.setDate(d.getDate() + 1);
  return d;
}

export function getRentalTypeDefaults(type) {
  if (!type || type === 'aangepaste_periode') return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (type === 'halve_dag') {
    const d = nextAvail(today, true);
    return {
      pickupDate: fmtDate(d),
      returnDate: fmtDate(d),
      pickupTime: DEFAULT_PICKUP_TIME,
      returnTime: getHalfDayReturnTime(DEFAULT_PICKUP_TIME)
    };
  }
  if (type === 'dag') {
    // 1 huurdag (UX-voorzet).
    //
    // Business rules:
    //   - Op zondag is VSB gesloten → ophalen mag nooit op zondag.
    //     Valt "morgen" op zondag, dan schuiven we door naar maandag.
    //   - Ophalen op zaterdag = 1 huurdag in het weekend; inleveren
    //     moet vóór 15:00 dezelfde zaterdag (winkel sluit). Uitzondering:
    //     bedrijfswagens en aanhangers — die kunnen aan de voorzijde in
    //     de parkeervakken geparkeerd worden, dus daarvoor stellen we
    //     normaal "pickup + 1" voor (= zondag 08:00).
    //   - Anders: ophalen = morgen, inleveren = de dag erna 08:00.
    //
    // De "park-zelf-vooraan"-uitzondering wordt via een globale flag
    // doorgegeven (gezet door form2.tpl op basis van product/categorie).
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const pickup = new Date(today);
    pickup.setDate(today.getDate() + 1);
    if (pickup.getDay() === 0) {
      pickup.setDate(pickup.getDate() + 1);
    }
    const parksOutFront = !!(
      typeof window !== 'undefined' && window.f2rParksOutFront
    );
    return getOneDayRentalDatesForPickup(fmtDate(pickup), {
      allowSundayReturn: parksOutFront
    });
  }
  if (type === 'weekend') {
    const saturday = new Date(today);
    let daysUntilSat = (6 - today.getDay() + 7) % 7;
    if (daysUntilSat === 0) daysUntilSat = 7;
    saturday.setDate(today.getDate() + daysUntilSat);
    while (typeof window.isClosedDay === 'function' && window.isClosedDay(saturday)) {
      saturday.setDate(saturday.getDate() + 7);
    }
    const monday = new Date(saturday);
    monday.setDate(saturday.getDate() + 2);
    while (typeof window.isClosedDay === 'function' && window.isClosedDay(monday)) {
      monday.setDate(monday.getDate() + 7);
    }
    return {
      pickupDate: fmtDate(saturday),
      returnDate: fmtDate(monday),
      pickupTime: DEFAULT_PICKUP_TIME,
      returnTime: DEFAULT_RETURN_TIME
    };
  }
  if (type === 'week') {
    const pw = nextAvail(today, true);
    const rw = new Date(pw);
    rw.setDate(pw.getDate() + 7);
    while (rw.getDay() === 0 || rw.getDay() === 6) rw.setDate(rw.getDate() + 1);
    return {
      pickupDate: fmtDate(pw),
      returnDate: fmtDate(skipClosed(rw)),
      pickupTime: DEFAULT_PICKUP_TIME,
      returnTime: DEFAULT_RETURN_TIME
    };
  }
  return null;
}

/** Minuten sinds middernacht (HH:mm). */
export function compareRentalTimes(a, b) {
  const parse = (t) => {
    const parts = String(t || '00:00').split(':');
    const h = Number(parts[0]) || 0;
    const m = Number(parts[1]) || 0;
    return h * 60 + m;
  };
  return parse(a) - parse(b);
}

/**
 * Huurdagen op basis van datums (08:00-grenzen) en optioneel tijden.
 * - Zelfde kalenderdag: 1 huurdag
 * - Verschillende datums: verschil in dagen (ophalen/inleveren standaard 08:00)
 * - Inleveren na 08:00 op de inleverdatum: +1 huurdag (deel van de volgende periode)
 */
export function calcRentalDays(pickup, ret, pickupTime, returnTime) {
  if (!pickup || !ret) return null;
  if (compareYmd(ret, pickup) < 0) return null;
  const d1 = new Date(`${pickup}T12:00:00`);
  const d2 = new Date(`${ret}T12:00:00`);
  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return null;

  if (d1.toDateString() === d2.toDateString()) {
    return 1;
  }

  let days = Math.max(1, Math.round((d2 - d1) / (1000 * 60 * 60 * 24)));
  const rt = returnTime || DEFAULT_RETURN_TIME;
  if (compareRentalTimes(rt, DEFAULT_RETURN_TIME) > 0) {
    days += 1;
  }
  return days;
}

function parseYmdDate(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const d = new Date(`${ymd}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Halve dag: alleen maandag t/m vrijdag (geen zaterdag/zondag). */
export function isHalfDayAllowedDate(ymd) {
  const d = parseYmdDate(ymd);
  if (!d) return false;
  const dow = d.getDay();
  return dow >= 1 && dow <= 5;
}

/** Eerstvolgende ma–vr op of na ymd. */
export function nextHalfDayAllowedDate(ymd) {
  const d = parseYmdDate(ymd);
  if (!d) return ymd || '';
  const next = new Date(d);
  while (next.getDay() === 0 || next.getDay() === 6) {
    next.setDate(next.getDate() + 1);
  }
  return fmtDate(next);
}

/** Corrigeer weekend-datums voor halve-daghuur (zonder melding). */
export function normalizeHalfDayPickupDate(ymd) {
  if (!ymd) return '';
  return isHalfDayAllowedDate(ymd) ? ymd : nextHalfDayAllowedDate(ymd);
}

/** Of een productpagina-bootstrap een halve-dagtarief toont. */
export function productOffersHalfDay(product) {
  const tiers = product?.priceTiers;
  if (!Array.isArray(tiers) || !tiers.length) return false;
  return tiers.some((tier) => {
    const label = String(tier?.label || '').toLowerCase();
    return label.includes('halve dag');
  });
}

/** Zelfde dag ophalen/terugbrengen → halve dag; dan za/zo blokkeren. */
export function shouldBlockHalfDayWeekends(pickup, ret) {
  if (!pickup || !ret) return false;
  return pickup === ret;
}

/** Vergelijk Y-m-d strings (-1 / 0 / 1); ongeldig → null. */
export function compareYmd(a, b) {
  if (!a || !b || !/^\d{4}-\d{2}-\d{2}$/.test(a) || !/^\d{4}-\d{2}-\d{2}$/.test(b)) return null;
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/** VSB gesloten op zondag; inleveren op zondag alleen bedrijfswagens/aanhangers (sleutelkluis). */
export function getParksOutFrontFlag() {
  return !!(typeof window !== 'undefined' && window.f2rParksOutFront);
}

/** Ophalen op zondag is nooit mogelijk (winkel gesloten). */
export function isPickupDateAllowed(ymd) {
  const d = parseYmdDate(ymd);
  if (!d) return false;
  return d.getDay() !== 0;
}

/** Inleveren op zondag alleen bij dropbox-producten (bedrijfswagen/aanhanger). */
export function isReturnDateAllowed(ymd, allowSundayReturn = getParksOutFrontFlag()) {
  const d = parseYmdDate(ymd);
  if (!d) return false;
  if (d.getDay() === 0) return !!allowSundayReturn;
  return true;
}

/** Eerstvolgende maandag na een zondag (of dezelfde datum als het geen zondag is). */
export function nextOpenDayAfterSunday(ymd) {
  const d = parseYmdDate(ymd);
  if (!d) return ymd || '';
  if (d.getDay() !== 0) return ymd;
  const next = new Date(d);
  next.setDate(next.getDate() + 1);
  return fmtDate(next);
}

/** Of ophaal-/inlevertijden bij een halve-daghuur horen (08–12 of 13–17). */
export function isHalfDayRentalTimes(pickupTime, returnTime) {
  const pt = String(pickupTime || DEFAULT_PICKUP_TIME);
  const rt = String(returnTime || '');
  return (
    (pt === '08:00' && rt === '12:00') ||
    (pt === '13:00' && rt === '17:00')
  );
}

/**
 * Inleverdatum/-tijd voor 1 huurdag bij een gekozen ophaaldatum (zelfde regels als getRentalTypeDefaults('dag')).
 */
export function getOneDayRentalDatesForPickup(pickupYmd, options = {}) {
  if (!pickupYmd) return null;
  const pickup = parseYmdDate(pickupYmd);
  if (!pickup) return null;

  const allowSundayReturn =
    options.allowSundayReturn !== undefined
      ? !!options.allowSundayReturn
      : getParksOutFrontFlag();

  if (pickup.getDay() === 6 && !allowSundayReturn) {
    return {
      pickupDate: pickupYmd,
      returnDate: pickupYmd,
      pickupTime: DEFAULT_PICKUP_TIME,
      returnTime: '15:00'
    };
  }

  const ret = new Date(pickup);
  ret.setDate(pickup.getDate() + 1);
  const normalized = normalizeRentalDateRange(pickupYmd, fmtDate(ret), { allowSundayReturn });
  return {
    pickupDate: normalized.pickupDate,
    returnDate: normalized.returnDate,
    pickupTime: DEFAULT_PICKUP_TIME,
    returnTime: DEFAULT_RETURN_TIME
  };
}

export function normalizePickupDate(ymd) {
  if (!ymd) return '';
  return isPickupDateAllowed(ymd) ? ymd : nextOpenDayAfterSunday(ymd);
}

export function normalizeReturnDate(ymd, allowSundayReturn = getParksOutFrontFlag()) {
  if (!ymd) return '';
  return isReturnDateAllowed(ymd, allowSundayReturn) ? ymd : nextOpenDayAfterSunday(ymd);
}

/** Inleverdatum mag niet vóór ophaaldatum liggen. */
export function normalizeRentalDateRange(pickup, ret, options = {}) {
  const allowSundayReturn =
    options.allowSundayReturn !== undefined
      ? !!options.allowSundayReturn
      : getParksOutFrontFlag();

  let pickupDate = normalizePickupDate(pickup || '');
  let returnDate = ret || '';

  if (pickupDate && returnDate && compareYmd(returnDate, pickupDate) < 0) {
    returnDate = pickupDate;
  }

  const d1 = parseYmdDate(pickupDate);
  const d2 = parseYmdDate(returnDate);

  // Za ophalen + zo terugbrengen → ma inleveren, behalve dropbox-producten (zo inleveren OK).
  if (d1 && d2 && d1.getDay() === 6 && d2.getDay() === 0 && !allowSundayReturn) {
    const monday = new Date(d2);
    monday.setDate(monday.getDate() + 1);
    returnDate = fmtDate(monday);
  } else {
    returnDate = normalizeReturnDate(returnDate, allowSundayReturn);
  }

  return { pickupDate, returnDate };
}

/** Za 08:00 t/m ma 08:00 — ophalen zaterdag, inleveren maandag (2 kalenderdagen). */
export function isWeekendRentalRange(pickup, ret) {
  if (!pickup || !ret) return false;
  const d1 = parseYmdDate(pickup);
  const d2 = parseYmdDate(ret);
  if (!d1 || !d2) return false;
  const calDiff = Math.max(0, Math.round((d2 - d1) / (1000 * 60 * 60 * 24)));
  return d1.getDay() === 6 && d2.getDay() === 1 && calDiff === 2;
}

/**
 * Stel huurtype en rentalDays af op basis van gekozen datums (widget → form2).
 * @returns {string|null} gekozen rentalType
 */
export function applyInferredRentalType(formState, prices = null, isOffered = null) {
  if (!formState?.pickupDate || !formState?.returnDate) return null;

  const inferred = inferRentalTypeFromDates(
    formState.pickupDate,
    formState.returnDate,
    formState.pickupTime,
    formState.returnTime,
    prices,
    isOffered
  );

  formState.rentalType = inferred;

  if (inferred === 'halve_dag') {
    formState.pickupDate = normalizeHalfDayPickupDate(formState.pickupDate);
    formState.returnDate = formState.pickupDate;
    formState.rentalDays = 0.5;
  } else if (inferred === 'dag') {
    formState.rentalDays = 1;
  } else {
    const countTimes =
      inferred === 'aangepaste_periode' || inferred === 'dag' || !inferred;
    const d = calcRentalDays(
      formState.pickupDate,
      formState.returnDate,
      countTimes ? formState.pickupTime : undefined,
      countTimes ? formState.returnTime : undefined
    );
    formState.rentalDays = d != null ? d : 1;
  }

  return inferred;
}

/**
 * Bepaal huurtype uit datums (zoekwidget product/categorie/homepage, URL, sessionStorage).
 * Zonder prijzen: heuristiek op datumpatroon. Met prijzen: eerste passend én aangeboden type.
 */
export function inferRentalTypeFromDates(
  pickup,
  ret,
  pickupTime = DEFAULT_PICKUP_TIME,
  returnTime = DEFAULT_RETURN_TIME,
  prices = null,
  isOffered = null
) {
  if (!pickup || !ret) return 'aangepaste_periode';

  const days = calcRentalDays(pickup, ret, pickupTime, returnTime) ?? 1;
  const d1 = parseYmdDate(pickup);
  const d2 = parseYmdDate(ret);
  const calDiff = d1 && d2 ? Math.max(0, Math.round((d2 - d1) / (1000 * 60 * 60 * 24))) : 0;
  const pickupDow = d1 ? d1.getDay() : -1;
  const returnDow = d2 ? d2.getDay() : -1;

  const candidates = [];

  if (isWeekendRentalRange(pickup, ret)) {
    candidates.push('weekend');
  }
  if (days === 7) {
    candidates.push('week');
  }
  if (
    pickup === ret &&
    isHalfDayAllowedDate(pickup) &&
    isHalfDayRentalTimes(pickupTime, returnTime)
  ) {
    candidates.push('halve_dag');
  }
  if (days === 1) {
    candidates.push('dag');
  }

  const pick = (type) => {
    if (!prices || typeof isOffered !== 'function') return type;
    return isOffered(type, prices) ? type : null;
  };

  for (const type of candidates) {
    const chosen = pick(type);
    if (chosen) return chosen;
  }

  if (!prices || typeof isOffered !== 'function') {
    return candidates[0] || 'aangepaste_periode';
  }

  return 'aangepaste_periode';
}

/** Of inlevertijd na 08:00 een extra huurdag oplevert (voor toelichting in zijbalk). */
export function hasLateReturnExtraRentalDay(pickup, ret, returnTime) {
  if (!pickup || !ret) return false;
  const d1 = new Date(`${pickup}T12:00:00`);
  const d2 = new Date(`${ret}T12:00:00`);
  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return false;
  if (d1.toDateString() === d2.toDateString()) return false;
  return compareRentalTimes(returnTime || DEFAULT_RETURN_TIME, DEFAULT_RETURN_TIME) > 0;
}
